import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

const SUCCESS_BUTTON_FEEDBACK_DURATION = 3000;

const VARIANT_FETCH_ERROR_EVENT = 'variant:fetch-error';
const VARIANT_FETCH_ERROR_MESSAGE = 'Seçenek bilgisi yüklenemedi. Lütfen tekrar deneyin.';
const ADD_TO_CART_ERROR_MESSAGE = 'Sepete eklenemedi. Lütfen tekrar deneyin.';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   */
  handleClick() {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed the configured maximum.
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      if (!validation.canAdd) {
        return;
      }
    }
  }

  showSuccess() {
    if (this.refs.addToCartButton.dataset.puppet === 'true') return;

    const animationEnabled = this.dataset.addToCartAnimation === 'true';
    if (animationEnabled && !this.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
    this.animateAddToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  async #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    try {
      await import('@theme/fly-to-cart');
    } catch {
      return;
    }

    if (!cartIcon.isConnected || !addToCartButton.isConnected || !customElements.get('fly-to-cart')) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    const flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, SUCCESS_BUTTON_FEEDBACK_DURATION);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {Set<number>} */
  #postActionTimeouts = new Set();

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {boolean} */
  #isSubmitting = false;

  /** @type {boolean} */
  #currentVariantAvailable = true;

  /** @type {Map<AddToCartComponent, boolean>} */
  #submissionButtonStates = new Map();

  /** @type {Map<AddToCartComponent, boolean>} */
  #variantButtonStates = new Map();

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    target?.addEventListener(VARIANT_FETCH_ERROR_EVENT, this.#onVariantFetchError, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    const initialButton = this.refs.addToCartButtonContainer?.refs?.addToCartButton;
    this.#currentVariantAvailable = initialButton ? !initialButton.disabled : Boolean(this.refs.variantId.value);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = undefined;
    }
    for (const timeout of this.#postActionTimeouts) {
      clearTimeout(timeout);
    }
    this.#postActionTimeouts.clear();
  }

  #schedulePostAction(callback, delay) {
    const timeout = setTimeout(() => {
      this.#postActionTimeouts.delete(timeout);
      if (!this.isConnected) return;
      callback();
    }, delay);
    this.#postActionTimeouts.add(timeout);
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#isSubmitting) return;

    if (this.#variantChangeInProgress) {
      if (this.#addToCartQueue.length > 0) return;

      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /** @returns {AddToCartComponent[]} */
  #getAddToCartContainers() {
    return Array.from(this.querySelectorAll('add-to-cart-component'));
  }

  /** @returns {boolean} */
  #beginSubmission() {
    if (this.#isSubmitting) return false;

    this.#setSuccessPanelVisible(false);
    this.#isSubmitting = true;
    this.#submissionButtonStates.clear();

    for (const container of this.#getAddToCartContainers()) {
      const button = container.refs.addToCartButton;
      this.#submissionButtonStates.set(container, button.disabled);
      container.disable();
      button.setAttribute('aria-busy', 'true');
    }

    return true;
  }

  #finishSubmission() {
    for (const container of this.#getAddToCartContainers()) {
      const button = container.refs.addToCartButton;
      const wasDisabled = this.#submissionButtonStates.get(container) ?? false;

      if (wasDisabled || this.#variantChangeInProgress || !this.#currentVariantAvailable) {
        container.disable();
      } else {
        container.enable();
      }

      button.removeAttribute('aria-busy');
    }

    this.#submissionButtonStates.clear();
    this.#isSubmitting = false;
  }

  #beginVariantWaiting() {
    if (this.#variantChangeInProgress) return;

    this.#setSuccessPanelVisible(false);
    this.#variantChangeInProgress = true;
    this.#variantButtonStates.clear();

    for (const container of this.#getAddToCartContainers()) {
      const button = container.refs.addToCartButton;
      this.#variantButtonStates.set(container, button.disabled);
      container.disable();
      button.setAttribute('aria-busy', 'true');
    }
  }

  /** @param {boolean} restoreButtonState */
  #finishVariantWaiting(restoreButtonState) {
    this.#variantChangeInProgress = false;

    for (const container of this.#getAddToCartContainers()) {
      const button = container.refs.addToCartButton;

      if (restoreButtonState) {
        const wasDisabled = this.#variantButtonStates.get(container) ?? false;
        if (wasDisabled) {
          container.disable();
        } else {
          container.enable();
        }
      }

      button.removeAttribute('aria-busy');
    }

    this.#variantButtonStates.clear();
  }

  /** @returns {HTMLElement | null} */
  #getErrorElement() {
    const existing = this.refs.addToCartTextError || this.querySelector('[data-product-form-error-container]');
    if (existing instanceof HTMLElement) return existing;

    const form = this.querySelector('form');
    if (!form) return null;

    const errorElement = document.createElement('span');
    errorElement.className = 'product-form-text__error hidden';
    errorElement.dataset.productFormErrorContainer = '';
    form.prepend(errorElement);
    return errorElement;
  }

  /** @param {string} message */
  #showError(message) {
    if (this.#timeout) clearTimeout(this.#timeout);
    this.#setSuccessPanelVisible(false);

    const errorElement = this.#getErrorElement();
    if (errorElement) {
      let messageElement = errorElement.querySelector('[data-product-form-error-message]');
      if (!(messageElement instanceof HTMLElement)) {
        messageElement = document.createElement('span');
        messageElement.dataset.productFormErrorMessage = '';
        errorElement.appendChild(messageElement);
      }

      messageElement.textContent = message;
      errorElement.classList.remove('hidden');
    }

    this.#setLiveRegionText(message);
    this.#timeout = setTimeout(() => {
      this.#hideError();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  #hideError() {
    const errorElement = this.refs.addToCartTextError || this.querySelector('[data-product-form-error-container]');
    errorElement?.classList.add('hidden');
    this.#clearLiveRegionText();
  }

  /**
   * @param {Response} response
   * @returns {Promise<Record<string, any>>}
   */
  async #parseCartResponse(response) {
    let data;

    try {
      data = await response.json();
    } catch {
      throw new Error(ADD_TO_CART_ERROR_MESSAGE);
    }

    if (!data || typeof data !== 'object') {
      throw new Error(ADD_TO_CART_ERROR_MESSAGE);
    }

    if (!response.ok && !data.status) {
      return { ...data, status: response.status };
    }

    return data;
  }

  /** @param {Record<string, any>} response */
  #getServerErrorMessage(response) {
    if (typeof response.message === 'string' && response.message.trim()) return response.message;
    if (typeof response.description === 'string' && response.description.trim()) return response.description;
    return ADD_TO_CART_ERROR_MESSAGE;
  }

  /** @param {boolean} visible */
  #setSuccessPanelVisible(visible) {
    const panel = /** @type {HTMLElement | null} */ (this.querySelector('[data-product-form-success]'));
    if (panel) panel.hidden = !visible;
  }

  /** @param {string} message */
  #showSuccessfulAdd(message) {
    this.#hideError();
    for (const container of this.#getAddToCartContainers()) {
      container.showSuccess();
    }
    this.#setSuccessPanelVisible(true);
    this.#setLiveRegionText(message);
    this.#schedulePostAction(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  async #processAddToCart(overrideVariantId, overrideQuantity, event) {
    if (this.#timeout) clearTimeout(this.#timeout);
    this.#hideError();
    this.#setSuccessPanelVisible(false);

    const allAddToCartContainers = this.#getAddToCartContainers();

    if (!overrideVariantId) {
      const anyButtonDisabled = allAddToCartContainers.some((container) => container.refs.addToCartButton?.disabled);
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) {
      this.#showError(ADD_TO_CART_ERROR_MESSAGE);
      return;
    }

    if (!form.reportValidity()) return;

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage =
          errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '') || ADD_TO_CART_ERROR_MESSAGE;
        this.#showError(errorMessage);

        this.#schedulePostAction(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length > 0) {
      formData.set('sections', cartItemComponentsSectionIds.join(','));
    }

    const id = formData.get('id');
    if (!id) {
      this.#showError(ADD_TO_CART_ERROR_MESSAGE);
      return;
    }

    if (!this.#beginSubmission()) return;

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const httpResponse = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });
      const response = await this.#parseCartResponse(httpResponse);

      if (response.status) {
        const message = this.#getServerErrorMessage(response);
        this.dispatchEvent(
          new CartErrorEvent(form.getAttribute('id') || '', message, response.description, response.errors)
        );
        this.#showError(message);

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
          })
        );
        return;
      }

      const anyAddToCartButton = allAddToCartContainers[0]?.refs?.addToCartButton;
      const addedTextElement = anyAddToCartButton?.querySelector('.add-to-cart__added-label');
      const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
      this.#showSuccessfulAdd(addedText);

      const cart = await this.#fetchAndUpdateCartQuantity();
      this.dispatchEvent(
        new CartAddEvent(cart ?? undefined, id.toString(), {
          source: 'product-form-component',
          itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
          productId: this.dataset.productId,
          sections: response.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', ADD_TO_CART_ERROR_MESSAGE, {}, {}));
      this.#showError(ADD_TO_CART_ERROR_MESSAGE);
    } finally {
      this.#finishSubmission();
      if (event) {
        cartPerformance.measureFromEvent('add:user-action', event);
      }
    }
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  async #processBatchAddToCart(items) {
    if (items.length === 0) return;
    if (!this.#beginSubmission()) return;

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#hideError();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    try {
      const httpResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const response = await this.#parseCartResponse(httpResponse);
      const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

      if (response.status) {
        const message = this.#getServerErrorMessage(response);
        this.dispatchEvent(new CartErrorEvent(this.id, message, response.description, response.errors));
        this.#showError(message);
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
          })
        );
        return;
      }

      const anyAddToCartButton = this.#getAddToCartContainers()[0]?.refs?.addToCartButton;
      const addedTextElement = anyAddToCartButton?.querySelector('.add-to-cart__added-label');
      const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
      this.#showSuccessfulAdd(addedText);

      const cart = await this.#fetchAndUpdateCartQuantity();
      this.dispatchEvent(
        new CartAddEvent(cart ?? undefined, this.id, {
          source: 'product-form-component',
          itemCount: totalQuantity,
          productId: this.dataset.productId,
          sections: response.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.dispatchEvent(new CartErrorEvent(this.id, ADD_TO_CART_ERROR_MESSAGE, {}, {}));
      this.#showError(ADD_TO_CART_ERROR_MESSAGE);
    } finally {
      this.#finishSubmission();
    }
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';
    this.#currentVariantAvailable = event.detail.resource?.available === true;
    this.#finishVariantWaiting(false);

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs?.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) {
      this.#addToCartQueue = [];
      return;
    }

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    if (this.#addToCartQueue.length > 0) {
      const [queuedItem] = this.#addToCartQueue;
      this.#addToCartQueue = [];

      if (event.detail.resource?.available && queuedItem) {
        this.#processAddToCart(queuedItem.variantId, queuedItem.quantity);
      }
    }

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#beginVariantWaiting();
  };

  /** @param {CustomEvent<{productId?: string}>} event */
  #onVariantFetchError = (event) => {
    if (event.detail?.productId && event.detail.productId !== this.dataset.productId) return;

    this.#addToCartQueue = [];
    this.#finishVariantWaiting(true);
    this.#showError(VARIANT_FETCH_ERROR_MESSAGE);
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
